import React, { FC } from 'react'
import { withRouter } from 'react-router-dom'
import PaginationControls from 'components/PaginationControls'
import { RouteProps } from 'constants/routes'
import { Location, History } from 'history'

import {
  useVersioning,
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
} from 'modules/versioning'
import { useGetSurroundingArtboardsQuery } from 'gqlTypes'

interface LocationState {
  fromOverview?: boolean
}

interface ArtboardPaginationControlsProps
  extends RouteProps<'ARTBOARD_REVISION'> {
  location: Location<LocationState>
  history: History
  current: number
  total: number
  showNumbers: boolean
}

const ArtboardPaginationControls: FC<ArtboardPaginationControlsProps> = props => {
  const { match, location, history, current, total, ...rest } = props
  const {
    versionShortId,
    getPathname,
    setArtboardThumbnail,
    artboardThumbnail,
  } = useVersioning()
  const { shareID, permanentArtboardShortId } = match.params

  const fromOverview = location.state?.fromOverview

  const navigateTo = (pathname: string) =>
    history.push({
      pathname: pathname,
      state: { fromOverview },
    })

  const { data: artboardData, error } = useGetSurroundingArtboardsQuery({
    variables: {
      permanentArtboardShortId: permanentArtboardShortId,
      documentVersionShortId: versionShortId,
      shareIdentifier: shareID,
    },
  })

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  const nextArtboardShortId = artboardData?.artboard?.nextArtboard
    ?.permanentArtboardShortId as string

  const previousArtboardShortId = artboardData?.artboard?.previousArtboard
    ?.permanentArtboardShortId as string

  const nextPathname = getPathname({
    routeKey: 'ARTBOARD_DETAIL',
    routeParams: {
      shareID: shareID,
      permanentArtboardShortId: nextArtboardShortId,
    },
  })

  const previousPathname = getPathname({
    routeKey: 'ARTBOARD_DETAIL',
    routeParams: {
      shareID: shareID,
      permanentArtboardShortId: previousArtboardShortId,
    },
  })

  const currentPage = current ? current + 1 : 1

  const clearThumbnail = () =>
    artboardThumbnail !== '' && setArtboardThumbnail('')

  const handleClickNext = () => {
    clearThumbnail()
    navigateTo(nextPathname)
  }

  const handleClickPrevious = () => {
    clearThumbnail()
    navigateTo(previousPathname)
  }
  return (
    <PaginationControls
      current={currentPage}
      total={total}
      onClickNext={handleClickNext}
      onClickPrevious={handleClickPrevious}
      {...rest}
    />
  )
}

export default withRouter(ArtboardPaginationControls)
